import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  type Auth,
  type ConfirmationResult,
} from 'firebase/auth'

interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

let app: FirebaseApp | null = null
let auth: Auth | null = null
let googleProvider: GoogleAuthProvider | null = null
let appleProvider: OAuthProvider | null = null
let phoneConfirmation: ConfirmationResult | null = null
let recaptchaVerifier: RecaptchaVerifier | null = null
// The actual `<div>` Firebase's RecaptchaVerifier is bound to. We mount
// this as a body-detached element on every sendPhoneOtp call so React's
// vDOM never owns it — meaning React can't accidentally re-mount the
// node mid-flow, and grecaptcha's internal "already rendered" registry
// can't conflict with a stale React-owned id. The previous attempt
// (binding to a React-owned `<div id="recaptcha-container">`) is what
// produced the "reCAPTCHA has already been rendered in this element"
// error on the fix-number-and-retry flow. Same pattern rider webapp
// has been shipping cleanly since deploy day.
let recaptchaContainer: HTMLDivElement | null = null

/**
 * Idempotent + defensive: returns true on success, false when config is
 * missing required fields (api_key + project_id). Callers treat `false` as
 * "Firebase login is unavailable for this tenant" and hide social-login UI,
 * NOT crash. Email/password still works without Firebase.
 */
export function initFirebase(config: FirebaseConfig): boolean {
  if (!config?.apiKey || !config?.projectId) return false
  try {
    if (getApps().length > 0) {
      app = getApps()[0]
    } else {
      app = initializeApp(config)
    }
    auth = getAuth(app)
    googleProvider = new GoogleAuthProvider()
    googleProvider.addScope('email')
    googleProvider.addScope('profile')
    appleProvider = new OAuthProvider('apple.com')
    appleProvider.addScope('email')
    appleProvider.addScope('name')
    return true
  } catch (err) {
    console.error('[cravo] Firebase initialization failed:', err)
    return false
  }
}

export function isFirebaseInitialized(): boolean {
  return auth !== null
}

export function getFirebaseAuth(): Auth {
  if (!auth) throw new Error('Firebase not initialized. Call initFirebase() first.')
  return auth
}

export interface SocialSignInResult {
  idToken: string
  email: string | null
  displayName: string | null
  photoUrl: string | null
}

// ─── Google Sign In ───────────────────────────────────────────────

export async function signInWithGoogle(): Promise<SocialSignInResult> {
  if (!auth || !googleProvider) {
    throw new Error('Firebase not initialized. Call initFirebase() first.')
  }

  try {
    const result = await signInWithPopup(auth, googleProvider)
    const idToken = await result.user.getIdToken()

    return {
      idToken,
      email: result.user.email,
      displayName: result.user.displayName,
      photoUrl: result.user.photoURL,
    }
  } catch (error: unknown) {
    const firebaseError = error as { code?: string }
    if (firebaseError.code === 'auth/popup-closed-by-user') throw new Error('POPUP_CLOSED')
    if (firebaseError.code === 'auth/popup-blocked') throw new Error('POPUP_BLOCKED')
    if (firebaseError.code === 'auth/account-exists-with-different-credential') throw new Error('ACCOUNT_EXISTS_DIFFERENT_PROVIDER')
    if (firebaseError.code === 'auth/network-request-failed') throw new Error('NETWORK_ERROR')
    throw error
  }
}

// ─── Apple Sign In ────────────────────────────────────────────────

export async function signInWithApple(): Promise<SocialSignInResult> {
  if (!auth || !appleProvider) {
    throw new Error('Firebase not initialized. Call initFirebase() first.')
  }

  try {
    const result = await signInWithPopup(auth, appleProvider)
    const idToken = await result.user.getIdToken()

    return {
      idToken,
      email: result.user.email,
      displayName: result.user.displayName,
      photoUrl: result.user.photoURL,
    }
  } catch (error: unknown) {
    const firebaseError = error as { code?: string }
    if (firebaseError.code === 'auth/popup-closed-by-user') throw new Error('POPUP_CLOSED')
    if (firebaseError.code === 'auth/popup-blocked') throw new Error('POPUP_BLOCKED')
    if (firebaseError.code === 'auth/account-exists-with-different-credential') throw new Error('ACCOUNT_EXISTS_DIFFERENT_PROVIDER')
    if (firebaseError.code === 'auth/network-request-failed') throw new Error('NETWORK_ERROR')
    throw error
  }
}

// ─── Phone Sign In ────────────────────────────────────────────────

function clearRecaptcha(): void {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear()
    } catch {
      // Already cleared or never rendered — safe to ignore
    }
    recaptchaVerifier = null
  }
  // Detach the dynamic container we created on the last sendPhoneOtp
  // call. Leaving it attached lets grecaptcha's internal registry hold
  // a reference and reject the next RecaptchaVerifier on the same
  // element with "already been rendered". Removing the node entirely
  // is the only path that reliably resets grecaptcha state.
  if (recaptchaContainer?.parentNode) {
    recaptchaContainer.parentNode.removeChild(recaptchaContainer)
  }
  recaptchaContainer = null
}

export function resetPhoneAuth(): void {
  clearRecaptcha()
  phoneConfirmation = null
}

// `_containerId` is accepted for backwards compat with the previous
// signature (call sites still pass `'recaptcha-container'`); the
// in-JSX `<div id="recaptcha-container" />` placeholder is no longer
// read. We now mount the verifier into a fresh body-detached `<div>`
// each call so React-managed DOM can't be mutated under our feet, and
// any stale grecaptcha state tied to a previous element is impossible
// by construction. Matches the rider webapp's proven pattern.
export async function sendPhoneOtp(phoneNumber: string, _containerId?: string): Promise<void> {
  if (!auth) throw new Error('Firebase not initialized.')

  clearRecaptcha()

  if (typeof document === 'undefined') {
    throw new Error('Phone OTP requires a browser environment.')
  }
  recaptchaContainer = document.createElement('div')
  recaptchaContainer.style.display = 'none'
  document.body.appendChild(recaptchaContainer)
  recaptchaVerifier = new RecaptchaVerifier(auth, recaptchaContainer, {
    size: 'invisible',
  })

  try {
    phoneConfirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
  } catch (error: unknown) {
    // Tear down on failure so the next attempt gets a fresh widget
    clearRecaptcha()
    const firebaseError = error as { code?: string }
    if (firebaseError.code === 'auth/invalid-phone-number') throw new Error('INVALID_PHONE_NUMBER')
    if (firebaseError.code === 'auth/too-many-requests') throw new Error('TOO_MANY_REQUESTS')
    if (firebaseError.code === 'auth/network-request-failed') throw new Error('NETWORK_ERROR')
    throw error
  }
}

export async function verifyPhoneOtp(otp: string): Promise<SocialSignInResult> {
  if (!phoneConfirmation) throw new Error('No pending phone verification. Call sendPhoneOtp() first.')

  try {
    const result = await phoneConfirmation.confirm(otp)
    const idToken = await result.user.getIdToken()

    // Success — tear down so any future flow starts fresh.
    phoneConfirmation = null
    clearRecaptcha()

    return {
      idToken,
      email: result.user.email,
      displayName: result.user.displayName,
      photoUrl: result.user.photoURL,
    }
  } catch (error: unknown) {
    const firebaseError = error as { code?: string }

    // INVALID_OTP: keep confirmation so the user can retry without re-sending.
    if (firebaseError.code === 'auth/invalid-verification-code') {
      throw new Error('INVALID_OTP')
    }
    // OTP_EXPIRED: confirmation is no longer usable — force a fresh send.
    if (firebaseError.code === 'auth/code-expired') {
      phoneConfirmation = null
      clearRecaptcha()
      throw new Error('OTP_EXPIRED')
    }
    // Any other error — assume confirmation is unusable, force fresh send.
    phoneConfirmation = null
    clearRecaptcha()
    throw error
  }
}

// ─── Sign Out ─────────────────────────────────────────────────────

export async function signOutFirebase(): Promise<void> {
  clearRecaptcha()
  phoneConfirmation = null
  if (auth) {
    await auth.signOut()
  }
}
